import * as React from 'react'
import { useEffect, useMemo } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Field,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Switch,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import {
    AddFilled,
    AddRegular,
    ArrowDownloadFilled,
    ArrowDownloadRegular,
    BuildingFilled,
    BuildingRegular,
    bundleIcon,
    PersonFilled,
    PersonRegular,
} from '@fluentui/react-icons'
import { useDispatch } from 'react-redux'
import useModules from '../../hooks/module/useModules'
import useRoles, { Role } from '../../hooks/useRoles'
import { AppAction } from '../../config/appConfig'
import { MenuItemKey } from '../../components/MainMenu'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { ModuleType } from '../../context/GlobalContext'
import AttributesList from '../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import useSubmitDocumentGenerationRequest from '../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationType } from '../../hooks/document/useDocumentGenerationHandler'
import { SortDirection } from '../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterType } from '../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import { PartyOverviewItemDto } from '../../config/partyConfig'
import BaseHeaderItem from '../BaseOverview/BaseHeaderItem'
import { PartyListItem } from './PartyListItem'
import { FileAction } from '../../config/fileConfig'

export const useItemStyles = makeStyles({
    listPartyItem: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewAvatarWidth) var(--baseOverviewPhoneWidth) auto',
        gridTemplateRows: 'var(--baseOverviewAvatarWidth) auto',

        '& div[aria-label="hover"]': {
            display: 'none',
            height: '24px',
            marginTop: tokens.spacingVerticalXXS,
            marginBottom: tokens.spacingVerticalXXS,
        },

        '&:hover': {
            '& div[aria-label="hover"]': {
                display: 'flex',
            },
        },
    },
    listPartyItemOverflow: {
        overflowWrap: 'anywhere',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
    listPartyItemActions: {
        marginLeft: 'auto',
        position: 'relative',
        gap: tokens.spacingVerticalS,
    },
    listPartyItemFileList: {
        gap: tokens.spacingVerticalS,
        margin: tokens.spacingVerticalS,
    },
    listPartyItemFileItem: {
        display: 'grid',
        gridTemplateColumns: 'minmax(60px, 80px) auto',
    },
})

const AddIcon = bundleIcon(AddFilled, AddRegular)
const BuildingIcon = bundleIcon(BuildingFilled, BuildingRegular)
const DownloadIcon = bundleIcon(ArrowDownloadFilled, ArrowDownloadRegular)
const PersonIcon = bundleIcon(PersonFilled, PersonRegular)

export type PartyFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        showNatural?: boolean
        showLegal?: boolean
        showAll?: boolean
    }

export type PartySorting = {
    id: string
    sortValue1: string
    sortValue2: string
}

export type PartySortingType = BaseOverviewSortingType<PartySorting>

export type PartyLineItemProps = BaseOverviewLineItemProps<PartyOverviewItemDto, PartyFilterType, PartySortingType>

export function PartyFilter() {
    const { values, setFieldValue } = useFormikContext<PartyFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])

    return (
        <>
            <Field label={translate('party.filter.natural.title')} orientation={orientation}>
                <Switch
                    checked={values.showNatural ?? true}
                    label={values.showNatural ? translate('base.toggle.boolean.true') : translate('base.toggle.boolean.false')}
                    onChange={(_, data) => {
                        setFieldValue('showNatural', data.checked)
                    }}
                />
            </Field>
            <Field label={translate('party.filter.legal.title')} orientation={orientation}>
                <Switch
                    checked={values.showLegal ?? true}
                    label={values.showLegal ? translate('base.toggle.boolean.true') : translate('base.toggle.boolean.false')}
                    onChange={(_, data) => {
                        setFieldValue('showLegal', data.checked)
                    }}
                />
            </Field>
            <Field label={translate('party.filter.inactive.title')} orientation={orientation}>
                <Switch
                    checked={values.showAll ?? false}
                    label={values.showAll ? translate('base.toggle.boolean.true') : translate('base.toggle.boolean.false')}
                    onChange={(_, data) => {
                        setFieldValue('showAll', data.checked)
                    }}
                />
            </Field>
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('party.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.Party}
                                context={[AttributeContextType.LegalPerson, AttributeContextType.NaturalPerson]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => setFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}

const headerSortingItems: PartySortingType[] = [
    {
        property: ['sortValue1', 'id'],
        direction: SortDirection.Ascending,
        default: true,
        descriptionKey: 'sorting.firstaz',
    },
    {
        property: ['sortValue1', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.firstza',
    },
    {
        property: ['sortValue2', 'id'],
        direction: SortDirection.Ascending,
        default: true,
        descriptionKey: 'sorting.lastaz',
    },
    {
        property: ['sortValue2', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.lastza',
    },
]

export function HeaderActionsButtons() {
    const history = useHistory()
    const overviewStyles = useOverviewStyles()
    const roles = useRoles()
    const { t: translate } = useTranslation()
    const { values } = useFormikContext<PartyFilterType>()

    const submitReportRequest = useSubmitDocumentGenerationRequest<PartyFilterType>(DocumentGenerationType.PartyXls)

    return (
        <>
            <Menu positioning={{ autoSize: true }}>
                <MenuTrigger disableButtonEnhancement>
                    <Tooltip withArrow relationship={'label'} content={translate('action.create.party.title')}>
                        <Button icon={<AddIcon />} appearance={'primary'} />
                    </Tooltip>
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        <MenuItem icon={<PersonIcon />} onClick={() => history.push('/party/natural/add')}>
                            {translate('action.create.person.natural.title')}
                        </MenuItem>
                        <MenuItem icon={<BuildingIcon />} onClick={() => history.push('/party/legal/add')}>
                            {translate('action.create.person.legal.title')}
                        </MenuItem>
                    </MenuList>
                </MenuPopover>
            </Menu>

            {roles.all([Role.Report]) && (
                <Tooltip withArrow relationship={'label'} content={translate('action.download.selection')}>
                    <Button icon={<DownloadIcon />} appearance={'secondary'} onClick={() => submitReportRequest(parseFilterValuesToParams(values))} />
                </Tooltip>
            )}
            <Button appearance={'secondary'} className={overviewStyles.hidden} />
        </>
    )
}

export default function PartyOverview() {
    const axios = useAxios()
    const dispatch = useDispatch()
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()

    //Reset breadcrumbs and data
    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Parties] })
        dispatch({ type: FileAction.ResetCurrent })
    }, [])

    const defaultFilterValues = useMemo<PartyFilterType>(
        () => ({
            showNatural: true,
            showLegal: true,
            showAll: false,
            attributes: [],
            query: '',
        }),
        [],
    )

    const countItems = (column?: number, filterValues?: PartyFilterType): Promise<BaseOverviewCount> => {
        return axios.get('/party/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        column?: number,
        filterValues?: PartyFilterType,
        sortingValues?: PartySortingType,
        start?: number,
        size?: number,
    ): Promise<PartyOverviewItemDto[]> => {
        return axios
            .get('/party', { params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) } })
            .then((response: AxiosResponse<PartyOverviewItemDto[]>) => response?.data || [])
    }

    return (
        <BaseOverview<PartyOverviewItemDto, PartyFilterType, PartySortingType>
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={BaseHeaderItem}
            listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
            listItemContent={props => <PartyListItem {...props} />}
            listItemClassnames={() => [itemStyles.listPartyItem]}
            headerActionsButtons={HeaderActionsButtons}
            headerSortingItems={headerSortingItems}
            filterContent={PartyFilter}
            inputPlaceholderKey={'party.filter.search'}
            localStorageKeyPrefix={'HammockPartyOverview'}
        />
    )
}
